import { Controller } from "@hotwired/stimulus"

const BAYER = [
  0, 32, 8, 40, 2, 34, 10, 42, 48, 16, 56, 24, 50, 18, 58, 26,
  12, 44, 4, 36, 14, 46, 6, 38, 60, 28, 52, 20, 62, 30, 54, 22,
  3, 35, 11, 43, 1, 33, 9, 41, 51, 19, 59, 27, 49, 17, 57, 25,
  15, 47, 7, 39, 13, 45, 5, 37, 63, 31, 55, 23, 61, 29, 53, 21
]

const smoothstep = (start, end, value) => {
  const t = Math.max(0, Math.min(1, (value - start) / (end - start)))
  return t * t * (3 - 2 * t)
}

export default class extends Controller {
  static targets = ["plate", "dots"]

  connect() {
    this.plateContext = this.plateTarget.getContext("2d")
    this.dotsContext = this.dotsTarget.getContext("2d")
    if (!this.plateContext || !this.dotsContext) return

    this.motion = window.matchMedia("(prefers-reduced-motion: reduce)")
    this.dark = window.matchMedia("(prefers-color-scheme: dark)")
    this.visible = true
    this.frameId = null
    this.lastFrame = 0
    this.started = 0
    this.clock = 0
    this.ripples = []
    this.onChange = () => this.sync()
    this.onResize = () => { this.resize(); if (this.frameId === null && this.visible && !document.hidden) this.paint(6) }
    this.onPointer = event => {
      if (this.motion.matches || this.frameId === null) return
      const bounds = this.element.getBoundingClientRect()
      this.ripples.push({ x: event.clientX - bounds.left, y: event.clientY - bounds.top, at: this.clock })
      if (this.ripples.length > 5) this.ripples.shift()
    }

    this.element.addEventListener("pointerdown", this.onPointer, { passive: true })
    document.addEventListener("visibilitychange", this.onChange)
    this.motion.addEventListener?.("change", this.onChange)
    this.dark.addEventListener?.("change", this.onChange)
    if (typeof ResizeObserver === "function") {
      this.resizeObserver = new ResizeObserver(this.onResize)
      this.resizeObserver.observe(this.element)
      this.resizeObserver.observe(this.plateTarget)
    } else {
      window.addEventListener("resize", this.onResize)
    }
    if (typeof IntersectionObserver === "function") {
      this.intersectionObserver = new IntersectionObserver(entries => {
        this.visible = entries[0].isIntersecting
        this.sync()
      }, { rootMargin: "120px" })
      this.intersectionObserver.observe(this.element)
    }

    this.resize()
    this.sync()
  }

  disconnect() {
    this.stop()
    this.resizeObserver?.disconnect()
    this.intersectionObserver?.disconnect()
    window.removeEventListener("resize", this.onResize)
    document.removeEventListener("visibilitychange", this.onChange)
    this.motion?.removeEventListener?.("change", this.onChange)
    this.dark?.removeEventListener?.("change", this.onChange)
    this.element.removeEventListener("pointerdown", this.onPointer)
  }

  sync() {
    if (!this.plateContext) return
    if (!this.visible || document.hidden) { this.stop(); return }
    this.readColors()
    if (this.motion.matches) {
      this.stop()
      this.paint(6)
      return
    }
    if (this.frameId === null) {
      this.started = 0
      this.lastFrame = 0
      this.frameId = requestAnimationFrame(ms => this.frame(ms))
    }
  }

  stop() {
    if (this.frameId !== null) cancelAnimationFrame(this.frameId)
    this.frameId = null
    this.ripples = []
  }

  frame(ms) {
    this.frameId = null
    if (!this.started) this.started = ms
    this.clock = (ms - this.started) / 1000
    if (ms - this.lastFrame >= 1000 / 15) {
      this.lastFrame = ms
      this.paint(this.clock)
    }
    this.frameId = requestAnimationFrame(next => this.frame(next))
  }

  resize() {
    const plate = this.plateTarget
    const width = Math.round(plate.clientWidth / 3)
    const height = Math.round(plate.clientHeight / 3)
    if (width < 8 || height < 8) return
    if (plate.width !== width || plate.height !== height) {
      plate.width = width
      plate.height = height
      this.image = this.plateContext.createImageData(width, height)
      this.radius = new Float32Array(width * height)
      this.falloff = new Float32Array(width * height)
      this.phase = new Float32Array(width * height)
      const cx = (width - 1) / 2
      const cy = (height - 1) / 2
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          const i = y * width + x
          const u = (x - cx) / cx
          const v = (y - cy) / cy
          const r = Math.hypot(u, v)
          const angle = Math.atan2(v, u)
          this.radius[i] = r
          this.phase[i] = 0.55 * Math.sin(angle * 3)
          this.falloff[i] = smoothstep(0.26, 0.52, r) *
            (1 - smoothstep(0.72, 1.02, r)) *
            (0.68 + 0.32 * Math.sin(angle * 2 + 0.7) ** 2) *
            (0.42 + 0.58 * smoothstep(0.565, 0.765, r))
        }
      }
    }

    const dots = this.dotsTarget
    const scale = Math.min(window.devicePixelRatio || 1, 2)
    const dotWidth = Math.round(dots.clientWidth * scale)
    const dotHeight = Math.round(dots.clientHeight * scale)
    if (dots.width !== dotWidth || dots.height !== dotHeight) {
      dots.width = dotWidth
      dots.height = dotHeight
      this.dotsContext.setTransform(scale, 0, 0, scale, 0, 0)
    }
  }

  readColors() {
    const styles = getComputedStyle(this.element)
    const parse = (name, fallback) => {
      const value = styles.getPropertyValue(name).trim()
      const match = value.match(/^#([\da-f]{6})$/i)
      if (!match) return fallback
      const code = parseInt(match[1], 16)
      return [(code >> 16) & 255, (code >> 8) & 255, code & 255]
    }
    const paper = parse("--canvas", [246, 247, 249])
    const blend = (name, fallback, amount) => parse(name, fallback)
      .map((channel, i) => Math.round(channel * (1 - amount) + paper[i] * amount))
    this.accent = blend("--accent", [4, 102, 244], 0.34)
    this.brand = blend("--brand", [43, 79, 153], 0.42)
    this.ink = parse("--ink", [10, 27, 42]).join(",")
  }

  paint(time) {
    this.resize()
    this.paintPlate(time)
    this.paintDots(time)
  }

  paintPlate(time) {
    if (!this.image) return
    const { width } = this.plateTarget
    const pixels = this.image.data
    for (let i = 0; i < this.radius.length; i++) {
      const r = this.radius[i]
      const wave = (1 + Math.sin(r * 13 - time * 0.95 + this.phase[i])) / 2
      const chop = (1 + Math.sin(r * 23 - time * 1.35 - this.phase[i])) / 2
      const value = (1.02 * wave ** 4 + 0.24 * chop ** 4) * this.falloff[i]
      const x = i % width
      const y = Math.floor(i / width)
      const threshold = (BAYER[(y % 8) * 8 + x % 8] + 0.5) / 64
      const offset = i * 4
      if (value > threshold) {
        const color = r > 0.8 ? this.brand : this.accent
        pixels[offset] = color[0]
        pixels[offset + 1] = color[1]
        pixels[offset + 2] = color[2]
        pixels[offset + 3] = 255
      } else {
        pixels[offset + 3] = 0
      }
    }
    this.plateContext.putImageData(this.image, 0, 0)
  }

  paintDots(time) {
    const context = this.dotsContext
    const width = this.dotsTarget.clientWidth
    const height = this.dotsTarget.clientHeight
    context.clearRect(0, 0, width, height)
    const centerX = width / 2
    const centerY = Math.min(height * 0.4, 270)
    this.ripples = this.ripples.filter(ripple => time - ripple.at < 1.9)
    for (let y = 1; y < height; y += 24) {
      for (let x = 1; x < width; x += 24) {
        const distance = Math.hypot(x - centerX, y - centerY)
        const crest = Math.max(0, Math.sin(distance * 0.0182 - time * 0.62))
        let opacity = distance < 1500 ? 0.085 * crest ** 3 * (1 - distance / 1500) : 0
        for (const ripple of this.ripples) {
          const age = time - ripple.at
          const delta = (Math.hypot(x - ripple.x, y - ripple.y) - age * 540) / 74
          opacity += 0.2 * (1 - age / 1.9) ** 2 * Math.exp(-delta * delta)
        }
        if (opacity < 0.016) continue
        context.fillStyle = `rgba(${this.ink},${Math.min(opacity, 0.3)})`
        context.fillRect(x - 0.6, y - 0.6, 1.2, 1.2)
      }
    }
  }
}
